import React from 'react'
import { red } from '@mui/material/colors'
import { createTheme } from '@mui/material/styles'
import { MFDiscoverButtonLoader } from './Components/ui/mf-discover-button-loader'

const muiLoadingButtonDefaults = {
  MuiLoadingButton: {
    defaultProps: {
      loadingIndicator: React.createElement(MFDiscoverButtonLoader, { className: 'h-5 w-14' }),
      loadingPosition: 'center',
    },
    styleOverrides: {
      root: {
        '&.MuiLoadingButton-loading': {
          color: 'transparent !important',
          textShadow: 'none',
        },
        '&.MuiLoadingButton-loading > :not(.MuiLoadingButton-loadingIndicator)': {
          opacity: 0,
        },
        '&.MuiLoadingButton-loading .MuiLoadingButton-loadingIndicator': {
          color: 'inherit',
          opacity: 1,
        },
        '&.MuiLoadingButton-loading .MuiLoadingButton-loadingIndicator img': {
          opacity: 1,
        },
      },
    },
  },
}

// A custom theme for this app
const darkTheme = createTheme({
  font: {
    fontFamily: 'Inter',
  },
  palette: {
    mode: 'dark',
    type: 'dark',
    background: {
      default: '#040313',
      paper: '#111225',
      primary: '#111225',
    },
    text: {
      primary: '#F6F6F9',
      secondary: '#C8C7D4',
      disabled: '#8588AB',
    },
    border: {
      primary: '1px solid rgba(213, 214, 226, 0.32)',
    },
    color: {
      default: '#F6F6F9',
    },
    primary: {
      main: '#2818FF',
      text: '#C8C7D4',
      textColor: '#F6F6F9',
    },
    secondary: {
      main: '#111225',
      text: '#F6F6F9',
    },
    tertiary: {
      main: '#17182A',
      text: '#F6F6F9',
    },
    error: {
      main: red.A400,
    },
    action: {
      disabledBackground: '#424462',
      disabled: '#8588AB',
    },
    typography: {
      allVariants: {
        color: '#fff',
      },
    },
  },
  components: {
    ...muiLoadingButtonDefaults,
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& .MuiTypography-root': {
            color: '#fff',
            fontFamily: 'DM Sans',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          input: {
            '&:-webkit-autofill': {
              '-webkit-text-fill-color': '#fff',
              fontFamily: 'DM Sans',
            },
            '&.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(255, 255, 255, 0.60)',
              fontFamily: 'DM Sans',
            },
          },
          '&.MuiSelect-root': {
            backgroundColor: '#0B0C1A',
            color: '#F6F6F9',
            fontFamily: 'DM Sans',
          },
          color: '#F6F6F9',
          borderColor: 'rgba(213, 214, 226, 0.32)',
        },
        notchedOutline: {
          borderColor: 'rgba(213, 214, 226, 0.32)',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          '&.MuiSelect-root': {
            backgroundColor: '#0B0C1A',
            fontFamily: 'DM Sans',
            color: '#F6F6F9',
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.38)',
            },
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.38) !important',
          },
          '&:before': {
            borderColor: 'rgba(213, 214, 226, 0.32)',
          },
          '&:hover&:before': {
            borderColor: 'rgba(213, 214, 226, 0.32)',
          },
          color: '#F6F6F9',
          borderBottomColor: 'rgba(213, 214, 226, 0.32)',
        },
        input: {
          color: '#F6F6F9',
          height: '30px',
          fontFamily: 'DM Sans',
          // fontSize: '20px',
          borderBottomColor: 'rgba(213, 214, 226, 0.32)',
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.38)',
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.38)',
          },
          '&:-webkit-autofill': {
            '-webkit-text-fill-color': '#fff',
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          color: '#F6F6F9',
          borderBottomColor: 'rgba(213, 214, 226, 0.32)',
          '&:before': {
            borderColor: 'rgba(213, 214, 226, 0.32)',
          },
          '&:hover&:before': {
            borderColor: 'rgba(213, 214, 226, 0.32)',
          },
        },
        input: {
          color: '#F6F6F9',
          borderBottomColor: 'rgba(213, 214, 226, 0.32)',
          '&:-webkit-autofill': {
            '-webkit-text-fill-color': '#fff',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#fff',
          fontSize: '17px',
          fontFamily: 'DM Sans',

          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.38)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#fff',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            backgroundColor: '#fff !important',
          },
          '& .MuiSwitch-thumb': {
            backgroundColor: '#000',
          },
          '& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
            backgroundColor: '#2818FF !important',
          },
          '& .Mui-checked .MuiSwitch-thumb': {
            backgroundColor: '#fff',
          },
          '& .MuiButtonBase-root.MuiSwitch-switchBase.Mui-disabled+.MuiSwitch-track': {
            opacity: 0.12,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          maxWidth: 'none',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&.MuiSelect-icon': {
            color: '#fff !important',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '.Mui-disabled.MuiTab-root': {
            color: 'rgba(255, 255, 255, 0.38) !important',
          },
        },
      },
    },
    // mui theme for responsiveness
  },
})

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    type: 'light',
    background: {
      default: '#FFFFFF',
      primary: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#040313',
      secondary: '#656992',
      disabled: '#B0B2C9',
    },
    border: {
      primary: '1px solid #D3DBEC',
    },
    borderRadius: {
      lg: '33px',
    },

    color: {
      default: '#000',
      upGreen: '#74A766',
      downRed: '#E85D5D',
    },
    primary: {
      main: '#2818FF',
      text: '#656992',
      textColor: '#040313',
    },
    secondary: {
      main: '#fff',
      text: '#000',
    },
    tertiary: {
      main: '#d1d2d2',
      text: '#000',
    },
    error: {
      main: red.A400,
    },
    action: {
      disabledBackground: '#808db3',
      disabled: '#FFFFFF',
    },
    typography: {
      allVariants: {
        color: '#000',
      },
    },
  },
  components: {
    ...muiLoadingButtonDefaults,
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&.MuiSelect-icon': {
            color: '#000 !important',
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& .MuiTypography-root': {
            color: '#000',
            fontFamily: 'DM Sans',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          input: {
            '&:-webkit-autofill': {
              '-webkit-text-fill-color': '#000',
            },
            '&.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.60)',
            },
          },
          '&.MuiSelect-root': {
            backgroundColor: '#fff',
            color: '#000',
          },
          color: '#000',
          borderColor: '#333',
        },
        notchedOutline: {
          borderColor: '#333',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          '&.MuiSelect-root': {
            backgroundColor: '#fff',
            color: '#000',
            fontFamily: 'DM Sans',
            '&.Mui-disabled': {
              color: 'rgba(0, 0, 0, 0.38)',
            },
          },
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.38) !important',
          },
          '&:before': {
            borderColor: '#333',
          },
          '&:hover&:before': {
            borderColor: '#333',
          },
          color: '#000',
          borderBottomColor: '#333',
        },
        input: {
          color: '#000',
          height: '30px',
          // fontSize: '20px',
          borderBottomColor: '#333',
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.38)',
            WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
          },
          '&:-webkit-autofill': {
            '-webkit-text-fill-color': '#000',
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          color: '#000',
          borderBottomColor: '#333',
          '&:before': {
            borderColor: '#333',
          },
          '&:hover&:before': {
            borderColor: '#333',
          },
        },
        input: {
          color: '#000',
          borderBottomColor: '#333',
          '&:-webkit-autofill': {
            '-webkit-text-fill-color': '#000',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#000',
          fontSize: '17px',
          fontFamily: 'DM Sans',

          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.38)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#333',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            backgroundColor: '#000 !important',
          },
          '& .MuiSwitch-thumb': {
            backgroundColor: '#fff',
          },
          '& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
            backgroundColor: '#2818FF !important',
          },
          '& .Mui-checked .MuiSwitch-thumb': {
            backgroundColor: '#000',
          },
          '& .MuiButtonBase-root.MuiSwitch-switchBase.Mui-disabled+.MuiSwitch-track': {
            opacity: 0.12,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          maxWidth: 'none',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '.Mui-disabled.MuiTab-root': {
            color: 'rgba(0, 0, 0, 0.38) !important',
          },
        },
      },
    },
    // mui theme for responsiveness
  },
})

export { lightTheme, darkTheme }
